/**
 * Espelho dos DTOs da API (`server/src/interfaces/http/mappers/dto.ts`).
 *
 * São escritos à mão de propósito: o front depende do contrato público da
 * API, não dos tipos internos do servidor. Se um dia o contrato ganhar um
 * gerador de tipos (OpenAPI), é este arquivo que ele substitui.
 */

export type RegimeTributario = 'simples_nacional' | 'lucro_presumido' | 'lucro_real';

export interface Pagina<T> {
  itens: T[];
  total: number;
  limite: number;
  deslocamento: number;
}

/**
 * O certificado A1 do cliente, como o painel o recebe. A senha não está aqui
 * — ela existe cifrada no servidor e não sai por rota nenhuma.
 */
export interface CertificadoDoCliente {
  nomeArquivo: string;
  titular: string;
  cnpj: string | null;
  cnpjFormatado: string | null;
  validoDe: string;
  validoAte: string;
  impressaoDigital: string;
  enviadoEm: string;
}

export interface Empresa {
  id: string;
  cnpj: string;
  cnpjFormatado: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  regimeTributario: RegimeTributario;
  ativa: boolean;
  emails: string[];
  contabilidadeId: string | null;
  certificado: CertificadoDoCliente | null;
  criadaEm: string;
}

/** A partir de quantos dias para o vencimento a tela começa a avisar. */
export const AVISO_DE_VENCIMENTO_DIAS = 30;

/**
 * Em que pé está o certificado, derivado da data — como `estaOnline`.
 *
 * O aviso de trinta dias existe porque renovar um A1 não é imediato: é
 * agendar validação com a certificadora. Um certificado que vence amanhã e
 * outro que vence em seis meses não podem ter a mesma cara na tela.
 */
export function situacaoDoCertificado(
  certificado: Pick<CertificadoDoCliente, 'validoAte'>,
  agora: number = Date.now(),
): { situacao: 'valido' | 'vencendo' | 'vencido'; dias: number } {
  const dias = Math.ceil(
    (new Date(certificado.validoAte).getTime() - agora) / (24 * 60 * 60 * 1000),
  );

  if (dias < 0) return { situacao: 'vencido', dias };
  return { situacao: dias <= AVISO_DE_VENCIMENTO_DIAS ? 'vencendo' : 'valido', dias };
}

export interface Contabilidade {
  id: string;
  cnpj: string;
  cnpjFormatado: string;
  razaoSocial: string;
  ativa: boolean;
  emails: string[];
  criadaEm: string;
}

export type Papel = 'administrador' | 'usuario' | 'contador';

export interface Usuario {
  id: string;
  nome: string;
  /** O login. É por ele que se entra, não por e-mail. */
  usuario: string;
  papel: Papel;
  /**
   * Preenchido só no papel `contador`: a contabilidade dele. É o que recorta
   * tudo que ele vê — e o recorte de verdade é o do servidor, que lê este
   * campo do cadastro e não da requisição.
   */
  contabilidadeId: string | null;
  ativo: boolean;
  criadoEm: string;
  ultimoAcessoEm: string | null;
}

export interface Sessao {
  token: string;
  expiraEm: string;
  usuario: Usuario;
}

export type SituacaoAgente = 'ativo' | 'bloqueado';

/** O PDV que gera as NFC-e na máquina do cliente. */
export type SistemaPdv = 'syspdv' | 'rk-pdv';

/** Como cada sistema é escrito na tela. */
export const ROTULO_DO_SISTEMA: Readonly<Record<SistemaPdv, string>> = {
  syspdv: 'SysPDV',
  'rk-pdv': 'RK-PDV',
};

/** Uma instalação do client numa máquina, com credencial própria. */
export interface Agente {
  id: string;
  empresaId: string;
  nome: string;
  /** Identidade da máquina, gerada pelo client na instalação. */
  identificadorDaMaquina: string | null;
  /** Parte pública do token: identifica a credencial sem revelá-la. */
  prefixoToken: string;
  situacao: SituacaoAgente;
  motivoDoBloqueio: string | null;
  /** `null` em instalação anterior ao campo. */
  sistema: SistemaPdv | null;
  /** De onde a instalação lê as notas. */
  origem: OrigemNotas;
  /** Vazio quando a origem é o banco do PDV. */
  pastaXml: string;
  /**
   * Conexão com o banco do PDV, sem a senha — ela fica só no `.ini` da
   * máquina. `null` quando a instalação lê de uma pasta.
   */
  banco: ConexaoDoPdv | null;
  intervaloSegundos: number;
  notificar: boolean;
  criadoEm: string;
  tokenEmitidoEm: string;
  bloqueadoEm: string | null;
  ultimoContatoEm: string | null;
  /** O último pedido de reenvio de período, em qualquer estágio. */
  varredura: VarreduraDoAgente | null;
  /**
   * Qual executável a máquina está rodando, como SHA-256 do arquivo.
   *
   * É hash e não número de versão porque ninguém precisa lembrar de
   * incrementá-lo ao publicar um agente novo: o arquivo é a versão. `null` é
   * instalação que nunca deu sinal, ou que roda um agente anterior ao relato.
   */
  versao: string | null;
  /** O último pedido de troca de executável, em qualquer estágio. */
  atualizacao: AtualizacaoDoAgente | null;
}

/**
 * Uma troca de executável pedida pelo escritório.
 *
 * Os marcos são os da varredura, e pelo mesmo motivo: `pedidaEm` sem
 * `entregueEm` é máquina desligada, `entregueEm` sem `concluidaEm` é troca em
 * andamento. A conclusão não é relatada pelo agente — ela é a versão da
 * máquina virar a pedida, e só o executável novo, já no ar, consegue
 * demonstrar isso.
 */
export interface AtualizacaoDoAgente {
  versaoAlvo: string;
  versaoAnterior: string | null;
  pedidaEm: string;
  entregueEm: string | null;
  concluidaEm: string | null;
  /** O que a máquina contou que deu errado; ela segue na versão de antes. */
  erro: string | null;
  erroEm: string | null;
}

/**
 * A versão em tamanho de tela: os primeiros dígitos do hash.
 *
 * O hash inteiro tem 64 caracteres e não cabe numa linha de tabela. Doze
 * bastam para alguém casar o que está na tela da loja com o que o escritório
 * publicou, que é a única pergunta que se faz dele.
 */
export function versaoCurta(versao: string | null): string {
  return versao === null ? 'não informada' : versao.slice(0, 12);
}

/**
 * Um reenvio de período pedido pelo escritório.
 *
 * Os três instantes contam a história inteira, e é por isso que são três:
 * `pedidaEm` sem `entregueEm` é máquina que ainda não falou — o pedido espera
 * a próxima presença —, `entregueEm` sem `concluidaEm` é varredura rodando no
 * PDV.
 */
export interface VarreduraDoAgente {
  de: string;
  ate: string;
  pedidaEm: string;
  entregueEm: string | null;
  concluidaEm: string | null;
  enviadas: number;
  repetidas: number;
  falhas: number;
}

export type OrigemNotas = 'pasta' | 'banco';

/**
 * A instalação está de pé agora?
 *
 * Não existe conexão aberta entre painel e máquina: o que há é o sinal que o
 * agente manda a cada rodada. Online, aqui, é "deu notícia dentro do prazo
 * esperado" — duas rodadas, mais um minuto de folga para rede lenta e para a
 * rodada que demorou lendo o banco. Abaixo disso, um pico de latência pintaria
 * de vermelho uma loja que está trabalhando.
 */
export function estaOnline(
  agente: Pick<Agente, 'ultimoContatoEm' | 'intervaloSegundos' | 'situacao'>,
  agora: number = Date.now(),
): boolean {
  if (agente.situacao !== 'ativo' || agente.ultimoContatoEm === null) return false;

  const tolerancia = (agente.intervaloSegundos * 2 + 60) * 1000;
  return agora - new Date(agente.ultimoContatoEm).getTime() <= tolerancia;
}

/** De onde a instalação lê, em uma linha, para as telas de agentes. */
export function descreverOrigem(agente: {
  origem: OrigemNotas;
  pastaXml: string;
  banco: ConexaoDoPdv | null;
}): string {
  if (agente.origem === 'banco' && agente.banco != null) {
    const rotulo = agente.banco.tipo === 'firebird' ? 'Firebird' : 'SQL Server';
    return `${rotulo} em ${agente.banco.servidor} · ${agente.banco.base}`;
  }
  // Instalação anterior ao campo não traz origem, e sempre lia de pasta.
  return agente.pastaXml === '' ? 'pasta não informada' : agente.pastaXml;
}
export type BancoPdv = 'firebird' | 'sqlserver';

export interface ConexaoDoPdv {
  tipo: BancoPdv;
  servidor: string;
  /** Caminho do `.fdb` (Firebird) ou nome da base (SQL Server). */
  base: string;
  usuario: string;
}

export type OperacaoFiscal = 'entrada' | 'saida';

/**
 * Em que pé a nota está no PDV da loja.
 *
 * Quem conta isso é o agente, pela chave: o XML não muda quando o cupom é
 * cancelado nem quando a SEFAZ rejeita — ele continua sendo o documento que a
 * loja emitiu. `pendente` espera resposta da SEFAZ e `rejeitada` voltou com
 * erro; nenhuma das duas é documento fiscal, e por isso ficam fora das somas
 * e do pacote da contabilidade.
 */
export type SituacaoFiscal = 'autorizada' | 'cancelada' | 'pendente' | 'rejeitada';

export interface ArquivoFiscal {
  id: string;
  empresaId: string;
  chave: string;
  /** `65` NFC-e, que é o que o client sincroniza, ou `55` NF-e já arquivada. */
  modelo: string;
  numero: string;
  serie: string;
  competencia: string;
  operacao: OperacaoFiscal;
  cnpjEmitente: string;
  cnpjEmitenteFormatado: string;
  cnpjDestinatario: string | null;
  valorCentavos: number;
  nomeArquivo: string;
  tamanhoBytes: number;
  emitidoEm: string;
  arquivadoEm: string;
  situacao: SituacaoFiscal;
  /** O que a SEFAZ respondeu: é a resposta de "por que não subiu?". */
  motivo: string | null;
  situacaoEm: string | null;
  /** `null` enquanto ninguém perguntou à SEFAZ por esta nota. */
  conferencia: ConferenciaSefaz | null;
  /** `null` enquanto o escritório não decidiu o que fazer com ela. */
  tratativa: Tratativa | null;
}

/** A última resposta que a SEFAZ deu sobre a nota, quando alguém perguntou. */
export interface ConferenciaSefaz {
  codigo: string;
  mensagem: string;
  protocolo: string | null;
  conferidaEm: string;
}

/**
 * O que o escritório fez com a pendência.
 *
 * `em_cobranca` é o único que não encerra: ele registra que alguém já pediu à
 * loja, para a pergunta "já falaram com eles?" ter resposta na segunda-feira
 * seguinte.
 */
export const DESFECHOS = [
  'reemitida',
  'inutilizada',
  'denegada',
  'descartada',
  'em_cobranca',
] as const;
export type Desfecho = (typeof DESFECHOS)[number];

export interface Tratativa {
  desfecho: Desfecho;
  observacao: string | null;
  /** A nota que ficou no lugar desta. Só existe em `reemitida`. */
  chaveSubstituta: string | null;
  usuarioId: string;
  registradaEm: string;
}

/** O rótulo de cada desfecho, e o que ele quer dizer em uma linha. */
export const DESFECHOS_DESCRITOS: Record<Desfecho, { rotulo: string; ajuda: string }> = {
  reemitida: {
    rotulo: 'Reemitida',
    ajuda: 'Saiu outra nota no lugar — informe a chave dela.',
  },
  inutilizada: {
    rotulo: 'Numeração inutilizada',
    ajuda: 'O número foi inutilizado na SEFAZ e não vai existir.',
  },
  denegada: {
    rotulo: 'Denegada',
    ajuda: 'A SEFAZ recusou por irregularidade fiscal: não há o que cobrar da loja.',
  },
  descartada: {
    rotulo: 'Descartada',
    ajuda: 'Nunca foi venda — teste, duplicidade ou engano. Diga o porquê.',
  },
  em_cobranca: {
    rotulo: 'Em cobrança com a loja',
    ajuda: 'Já pedimos à loja; a nota continua na fila até alguma coisa acontecer.',
  },
};

/** Os que tiram a nota da fila de trabalho; `em_cobranca` não entra. */
export const DESFECHOS_ENCERRADOS: readonly Desfecho[] = [
  'reemitida',
  'inutilizada',
  'denegada',
  'descartada',
];

/**
 * Um grupo da triagem: as pendências que a SEFAZ recusou pelo mesmo código.
 *
 * É a unidade de trabalho da tela, e não a nota: rejeição em NFC-e é quase
 * sempre o mesmo erro de configuração do PDV repetido em todas as vendas da
 * tarde.
 */
export interface GrupoDePendencia {
  codigo: string | null;
  mensagem: string | null;
  quantidade: number;
  pendentes: number;
  rejeitadas: number;
  resolvidas: number;
  maisAntigaEm: string;
  maisRecenteEm: string;
}

export interface TriagemDePendencias {
  empresaId: string;
  razaoSocial: string;
  cnpjFormatado: string;
  quantidade: number;
  resolvidas: number;
  grupos: GrupoDePendencia[];
}

/** O que uma rodada de conferência na SEFAZ conseguiu fazer. */
export interface ResultadoDaConferencia {
  razaoSocial: string;
  consultadas: number;
  autorizadas: number;
  canceladas: number;
  rejeitadas: number;
  /** Respostas que não falam da nota: código desconhecido, serviço fora. */
  inconclusivas: number;
  /** Quantas ficaram para a próxima rodada. */
  restantes: number;
  /** O que parou a rodada antes do fim, quando foi o caso. */
  interrompidaPor: string | null;
}

export interface ResumoArquivosFiscais {
  /** Conta as canceladas também: elas continuam no acervo. */
  quantidade: number;
  /** As somas deixam o cupom cancelado de fora — ele não é movimento. */
  entradasCentavos: number;
  saidasCentavos: number;
  /** Quantas das `quantidade` ficaram fora das somas. */
  canceladas: number;
  pendentes: number;
  rejeitadas: number;
}

/**
 * Uma loja com nota esperando alguém, na primeira aba de Pendências.
 *
 * Pendente e rejeitada não são documento fiscal: elas ficam fora do `.zip` da
 * contabilidade e fora da soma do período. Quem tem zero não aparece na
 * lista — a tela é das lojas que têm o que resolver.
 */
/**
 * Como está o mês de uma loja.
 *
 * `enviado` cobre os dois caminhos — o automático do dia 5 e o manual da tela
 * de arquivos fiscais —, e `sem_notas` é diferente de `nao_enviado`: a loja
 * que não vendeu nada não deve nada a ninguém.
 */
export type SituacaoMensal = 'enviado' | 'falhou' | 'sem_notas' | 'nao_enviado';

/** O filtro da tela: as situações mais a pergunta que não é situação. */
export type FiltroDaSituacaoMensal = SituacaoMensal | 'com_pendencia';

export interface SituacaoMensalDoCliente {
  empresaId: string;
  razaoSocial: string;
  cnpjFormatado: string;
  /** Cliente inativado aparece quando tem nota no mês: ela não deixou de existir. */
  ativa: boolean;
  situacao: SituacaoMensal;
  notas: number;
  pendentes: number;
  rejeitadas: number;
  /** Pendência da loja fora deste mês — o que ficou para trás. */
  pendentesEmOutrosMeses: number;
  enviadoEm: string | null;
  origemDoEnvio: 'fechamento' | 'manual' | null;
  erroDoEnvio: string | null;
}

/** A contagem por situação do mês inteiro, que não muda com o filtro. */
export interface ResumoDoMes {
  clientes: number;
  enviados: number;
  naoEnviados: number;
  falhas: number;
  semNotas: number;
  comPendencia: number;
}

export interface SituacaoDoMes {
  competencia: string;
  itens: SituacaoMensalDoCliente[];
  resumoDoMes: ResumoDoMes;
}

/** Um buraco na numeração do que o cliente emitiu, por modelo e série. */
export interface QuebraDeSequencia {
  modelo: string;
  serie: string;
  primeiroNumero: number;
  ultimoNumero: number;
  quantidade: number;
  /** Emissão da nota logo antes do buraco, e da logo depois. */
  anteriorEm: string;
  posteriorEm: string;
}

export interface SequenciaDoCliente {
  empresaId: string;
  razaoSocial: string;
  cnpjFormatado: string;
  /** Quantas notas emitidas pelo cliente entraram na conferência. */
  analisadas: number;
  /** Soma de todas as quebras, inclusive as que não couberam na lista. */
  faltando: number;
  /** Pode ser maior que `quebras.length`: a lista do servidor tem teto. */
  totalDeQuebras: number;
  quebras: QuebraDeSequencia[];
}

export interface PaginaDeArquivosFiscais {
  itens: ArquivoFiscal[];
  total: number;
  limite: number;
  deslocamento: number;
  resumo: ResumoArquivosFiscais;
}

/**
 * O que o servidor conta depois de mandar o período por e-mail.
 *
 * `destinatarios` volta na resposta de propósito: quem recebeu é decidido pelo
 * cadastro no instante do envio, e a tela precisa mostrar a lista de verdade,
 * não a que ela imaginou antes de clicar.
 *
 * Os links também só existem aqui — é a única vez que eles aparecem fora do
 * e-mail, a mesma regra do token do agente. É o que permite copiar o link para
 * quem pediu por outro canal sem precisar reenviar a mensagem.
 */
export interface ResultadoEnvioPorEmail {
  destinatarios: string[];
  quantidade: number;
  /** Baixa o `.zip` do período, com os XMLs e a planilha dentro. */
  linkDownload: string;
  /** Abre o portal, onde o contador escolhe outro período. */
  linkPortal: string;
  validoAte: string;
  /** `março de 2026`, ou o intervalo quando não é um mês inteiro. */
  rotuloDoPeriodo: string;
}

export interface CorpoErro {
  erro: {
    codigo: string;
    mensagem: string;
    detalhes?: Record<string, unknown>;
  };
}

/**
 * Apelido da interface: para o escritório, cada empresa atendida é um cliente.
 * O contrato da API continua falando em `Empresa`, que é o termo fiscal.
 */
export type Cliente = Empresa;

/* ---------------------------- log de e-mails ------------------------------- */

export const SITUACOES_DE_ENVIO = ['enviado', 'falhou'] as const;
export type SituacaoDeEnvio = (typeof SITUACOES_DE_ENVIO)[number];

export const ORIGENS_DE_ENVIO = [
  'fechamento-automatico',
  'fechamento-painel',
  'arquivos-fiscais',
  'outro',
] as const;
export type OrigemDeEnvio = (typeof ORIGENS_DE_ENVIO)[number];

/**
 * Uma linha do log de e-mails: uma mensagem que o sistema mandou — ou tentou.
 *
 * Não traz a URL do contador: ela aparece uma vez só, na resposta que a
 * emitiu, como o token do agente. O que vem aqui é o que responde ao telefone:
 * saiu, quando, para quem, de que período, e se o link foi aberto.
 */
export interface EnvioDeEmail {
  id: string;
  enviadoEm: string;
  situacao: SituacaoDeEnvio;
  origem: OrigemDeEnvio;
  assunto: string;
  destinatarios: string[];
  erro: string | null;
  empresaId: string | null;
  /** A razão social do momento do envio: renomear o cliente não reescreve o log. */
  razaoSocial: string | null;
  cnpjFormatado: string | null;
  /** `AAAA-MM-DD`. */
  periodoDe: string | null;
  periodoAte: string | null;
  rotuloDoPeriodo: string | null;
  /** `AAAA-MM`, quando o envio é de um fechamento. */
  competencia: string | null;
  quantidadeDeNotas: number | null;
  acessoPublicoId: string | null;
  linkAtivo: boolean;
  linkExpiraEm: string | null;
  linkUltimoAcessoEm: string | null;
  linkAcessos: number;
}

export interface PaginaDeEnvios {
  itens: EnvioDeEmail[];
  total: number;
  limite: number;
  deslocamento: number;
}

/* -------------------------- portal público do contador --------------------- */

/**
 * O cabeçalho do portal, lido a partir do token da URL.
 *
 * Quem responde "de quem são estes arquivos" é o servidor, nunca a página: o
 * cliente vem do token, e é isso que impede um link de alcançar o cliente de
 * outro escritório.
 */
export interface DadosDoPortal {
  cliente: {
    razaoSocial: string;
    nomeFantasia: string | null;
    cnpjFormatado: string;
  };
  /** O período do e-mail que trouxe o link, em `AAAA-MM-DD`. */
  periodoSugerido: { de: string; ate: string };
  expiraEm: string;
}
